import React from 'react';
import { useTranslation } from 'react-i18next';
import { buttonClasses } from 'components/common/tailwindClassnames';
import { AllowIcon, BlockIcon } from 'components/common/ProjectIcons';
import ManualBadge from './ManualBadge';
import { SourceSummary } from './sourceFilterTypes';

// One device row of the filter editor. The rule stores only exceptions, so a row is
// either MARKED (colored, chip, undo action) or NEUTRAL (plain, one action that would
// put it in the rule) — never a state chip on a row the rule says nothing about.
// Extracted because three tab views rendered these two shapes with copy-pasted markup
// that had already started drifting.

// Sizes the button to the wider of a mark/unmark label pair, so Block and Unblock
// (or Allow and Remove) are the same width in every language.
const PairedLabel = ({ shown, other }: { shown: string; other: string }) => (
  <span className="inline-grid">
    <span className="col-start-1 row-start-1">{shown}</span>
    <span aria-hidden="true" className="invisible col-start-1 row-start-1">
      {other}
    </span>
  </span>
);

type DeviceRowProps = {
  source: SourceSummary;
  marked: boolean;
  // blacklist rules tint marks red (blocked); whitelist rules tint them green (allowed).
  blocking: boolean;
  // A just-created row: tinted for a beat so the user sees where it landed,
  // then the transition fades it back to the mark's own tint.
  highlighted?: boolean;
  onMark: (source: SourceSummary) => void;
  onUnmark: (id: string) => void;
};

const DeviceRow = ({
  source,
  marked,
  blocking,
  highlighted,
  onMark,
  onUnmark,
}: DeviceRowProps) => {
  const { t } = useTranslation();
  // Two lines so the row isn't one crowded strip: what the source is and its
  // state on top, where it came from underneath, and the action to the right.
  const details = (statusTag?: React.ReactNode) => (
    <div className="flex min-w-0 flex-1 flex-col gap-1">
      <div className="flex min-w-0 items-center gap-2">
        <span className="break-all text-sm text-ui-text-primary-text">
          {source.external_id}
        </span>
        {statusTag}
      </div>
      <div className="flex min-w-0 items-center gap-2 empty:hidden">
        {source.name && (
          <span className="truncate text-xs text-ui-text-secondary-text">
            {t('"{{name}}"', { name: source.name })}
          </span>
        )}
        <ManualBadge source={source} testIdPrefix="SourceFiltersForm" />
      </div>
    </div>
  );
  if (marked) {
    return (
      <li
        className={`flex min-w-0 items-center gap-3 px-3 py-2 transition-colors duration-700 ${
          highlighted
            ? 'bg-teal-t30'
            : blocking
              ? 'bg-ui-status-bg-error-bg/40'
              : 'bg-teal-t10/40'
        }`}
        data-testid={`SourceFiltersForm-marked-${source.external_id}`}
        data-highlight-id={source.id}
      >
        {details(
          <span
            className={`inline-flex shrink-0 items-center gap-1 rounded px-1.5 py-0.5 text-xs font-semibold ${
              blocking
                ? 'bg-ui-status-bg-error-bg text-red-2'
                : 'bg-teal-t10 text-green-1'
            }`}
          >
            {blocking ? <BlockIcon size={12} /> : <AllowIcon size={12} />}
            {blocking ? t('Blocked') : t('Allowed')}
          </span>,
        )}
        <button
          type="button"
          onClick={() => onUnmark(source.id)}
          className={`ml-auto shrink-0 ${buttonClasses.cancelXs}`}
          data-testid={`SourceFiltersForm-unmark-${source.external_id}`}
        >
          <PairedLabel
            shown={blocking ? t('Unblock') : t('Remove')}
            other={blocking ? t('Block') : t('Allow')}
          />
        </button>
      </li>
    );
  }
  return (
    <li
      className="flex min-w-0 items-center gap-3 px-3 py-2"
      data-testid={`SourceFiltersForm-row-${source.external_id}`}
    >
      {details()}
      <button
        type="button"
        onClick={() => onMark(source)}
        className={`ml-auto shrink-0 ${buttonClasses.cancelXs}`}
        data-testid={`SourceFiltersForm-mark-${source.external_id}`}
      >
        <PairedLabel
          shown={blocking ? t('Block') : t('Allow')}
          other={blocking ? t('Unblock') : t('Remove')}
        />
      </button>
    </li>
  );
};

export default DeviceRow;
